import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button } from "@tellescope/ui";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AvailabilityBlock } from "./AvailabilityBlock";
import { UniqueWeeklyAvailability } from "./AvailabilityView";

export interface DayAvailabilityAccordionItemProps {
  day: string;
  dayIndex: number;
  blocks: UniqueWeeklyAvailability[];
  timezoneAbbreviation: string;
  formatTimeFromMinutes: (minutes: number) => string;
  onAddBlock: (dayIndex: number) => void;
  onBlockChange: (block: UniqueWeeklyAvailability) => void;
  onBlockDelete: (index: number) => void;
  canUpdate: boolean;
}

export const DayAvailabilityAccordionItem = ({
  day,
  dayIndex,
  blocks,
  timezoneAbbreviation,
  formatTimeFromMinutes,
  onAddBlock,
  onBlockChange,
  onBlockDelete,
  canUpdate,
}: DayAvailabilityAccordionItemProps) => {
  const { t } = useTranslation("calendar");

  const timeRanges = blocks
    .map(
      (block) =>
        `${formatTimeFromMinutes(block.startTimeInMinutes ?? 0)} - ${formatTimeFromMinutes(
          block.endTimeInMinutes ?? 0
        )} ${timezoneAbbreviation}`
    )
    .join(", ");

  return (
    <AccordionItem
      value={day}
      data-testid={`availability-day-${dayIndex}`}
      className="border rounded-sm last:border-b-1"
    >
      <AccordionTrigger
        data-testid={`availability-day-${dayIndex}-trigger`}
        className="flex items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-b-sm rounded-t-sm hover:no-underline"
        chevronPosition="leading"
        chevronDirection="right-to-down"
        shiftIcon={false}
      >
        <div className="grow grid grid-cols-[100px_auto_min-content] items-center">
          <p className="font-normal">{day}</p>
          <p className="font-light text-xs text-muted-foreground truncate text-nowrap">
            {blocks.length
              ? t("{{count}} blocks · {{timeRanges}}", {
                  count: blocks.length,
                  timeRanges,
                })
              : blocks.length
              ? t("{{count}} blocks", { count: blocks.length })
              : t("No availability")}
          </p>
          {!!blocks.length && (
            <p className="font-light text-[10px] rounded-full px-1 py-0.5 bg-muted">{blocks.length}</p>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col pb-0">
        {!!blocks.length && (
          <div className="px-4 py-3 flex flex-col gap-2 border-b">
            {blocks.map((block) => (
              <Accordion key={block._originalIndex} type="multiple">
                <AvailabilityBlock
                  block={block}
                  onChange={(block) => onBlockChange(block)}
                  onDelete={() => onBlockDelete(block._originalIndex)}
                  canUpdate={canUpdate}
                />
              </Accordion>
            ))}
          </div>
        )}
        <div className="px-4 py-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground font-light text-xs items-center flex leading-none"
            onClick={() => onAddBlock(dayIndex)}
            disabled={!canUpdate}
            data-testid={`availability-day-${dayIndex}-add-block`}
          >
            <Plus className="size-3" />
            {t("Add block")}
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
